import { useEffect, useMemo, useRef, useState } from "react"

import Spreadsheet from "react-spreadsheet"
import * as XLSX from "xlsx"

import { useDocumentPreviewSlowMs } from "@/features/chat/hooks/use-document-preview-slow-ms"
import { useFileContent } from "@/hooks/use-file-content"
import { logger } from "@/lib/logger"
import { scrollCitationHighlightIntoView } from "@/utils/dom-highlight"
import { findTextWithFuzzyMatching } from "@/utils/fuzzy-text-matcher"

import { FallbackViewer } from "./fallback-viewer"
import styles from "../styles/file-viewer.module.css"

type SpreadsheetCell = {
  value: string | number | boolean | Date
  readOnly: boolean
  className: string
}

type ExcelViewerProps = {
  fileUrl: string
  highlightQuote?: string | null
  highlightChunk?: string | null
  highlightScrollContainer?: HTMLElement | null
  /** Called when the parse completes slower than the heavy-preview threshold. */
  onSlowRender?: () => void
}

function normalizeCell(cell: unknown): string | number | boolean | Date {
  if (
    cell instanceof Date ||
    typeof cell === "string" ||
    typeof cell === "number" ||
    typeof cell === "boolean"
  ) {
    return cell
  }
  if (cell !== null && cell !== undefined) {
    return String(cell)
  }
  return ""
}

function rowsToSheetData(rows: unknown[][], baseCellClass: string): SpreadsheetCell[][] {
  return rows.map((row) =>
    row.map((cell) => ({
      value: normalizeCell(cell),
      readOnly: true,
      className: baseCellClass,
    }))
  )
}

type WorkbookState = {
  sheetNames: string[]
  sheetData: SpreadsheetCell[][][]
}

function parseWorkbook(buffer: ArrayBuffer): WorkbookState | null {
  try {
    const workbook = XLSX.read(buffer, { type: "array" })
    const sheetNames = workbook.SheetNames

    if (!sheetNames.length) {
      return null
    }

    const sheetData: SpreadsheetCell[][][] = sheetNames.map((name) => {
      const ws = workbook.Sheets[name]
      const ref = ws["!ref"]
      let rows: unknown[][] = []

      if (ref) {
        rows = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
          range: ref,
        }) as unknown[][]
      }

      if (!rows.length) {
        rows = [[]]
      }

      return rowsToSheetData(rows, styles.spreadSheetReadOnlyCell)
    })

    return { sheetNames, sheetData }
  } catch {
    return null
  }
}

/**
 * Renders read-only spreadsheet for `.xlsx` and `.xls` files.
 * Supports workbook tabs and citation highlighting when highlightQuote or highlightChunk is provided.
 */
export function ExcelViewer({
  fileUrl,
  highlightQuote,
  highlightChunk,
  highlightScrollContainer = null,
  onSlowRender,
}: ExcelViewerProps) {
  const slowThresholdMs = useDocumentPreviewSlowMs()

  // Use refs for slow-render detection so the expensive parse effect
  // doesn't re-run when the config query resolves or callbacks change.
  const slowThresholdRef = useRef(slowThresholdMs)
  slowThresholdRef.current = slowThresholdMs
  const onSlowRenderRef = useRef(onSlowRender)
  onSlowRenderRef.current = onSlowRender

  const { data: buffer, isError, isLoading } = useFileContent(fileUrl)

  const [workbook, setWorkbook] = useState<WorkbookState | null>(null)
  const [activeSheetIndex, setActiveSheetIndex] = useState(0)
  const spreadsheetWrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!buffer) {
      setWorkbook(null)
      return
    }

    const parseStart = performance.now()
    const parsed = parseWorkbook(buffer)
    setWorkbook(parsed)
    setActiveSheetIndex(0)

    const elapsed = performance.now() - parseStart
    if (elapsed > slowThresholdRef.current) {
      logger.info("excel-viewer slow parse detected", { elapsed: Math.round(elapsed) })
      onSlowRenderRef.current?.()
    }
  }, [buffer])

  useEffect(() => {
    const searchText = highlightQuote ?? highlightChunk ?? null
    if (!workbook || !searchText?.trim()) return

    const firstMatchingIndex = workbook.sheetData.findIndex((sheet) =>
      sheet.some((row) =>
        row.some((cell) => {
          const cellStr = String(cell.value)
          return findTextWithFuzzyMatching(cellStr, searchText).found
        })
      )
    )
    if (firstMatchingIndex !== -1) {
      setActiveSheetIndex(firstMatchingIndex)
    }
  }, [workbook, highlightQuote, highlightChunk])

  const currentData = useMemo(() => {
    if (!workbook) return []
    const base = workbook.sheetData[activeSheetIndex] ?? []
    const searchText = highlightQuote ?? highlightChunk ?? null
    if (!searchText || !searchText.trim()) {
      return base
    }
    return base.map((row) =>
      row.map((cell) => {
        const cellStr = String(cell.value)
        const match = findTextWithFuzzyMatching(cellStr, searchText)
        const className = match.found
          ? `${cell.className} ${styles.citationHighlight}`.trim()
          : cell.className
        return { ...cell, className }
      })
    )
  }, [workbook, activeSheetIndex, highlightQuote, highlightChunk])

  const hasHighlight = Boolean((highlightQuote ?? highlightChunk)?.trim())

  useEffect(() => {
    if (!hasHighlight || !spreadsheetWrapperRef.current) return

    let cancelScroll = () => {}
    const firstHighlighted = spreadsheetWrapperRef.current.querySelector(
      `.${styles.citationHighlight}`
    ) as HTMLElement | null
    if (firstHighlighted) {
      cancelScroll = scrollCitationHighlightIntoView(firstHighlighted, {
        preferredContainer: highlightScrollContainer ?? spreadsheetWrapperRef.current,
        maxPasses: 2,
      })
    }

    return () => cancelScroll()
  }, [hasHighlight, activeSheetIndex, highlightScrollContainer])

  if (isError) {
    return <FallbackViewer fileUrl={fileUrl} />
  }

  if (isLoading || !buffer) {
    return (
      <div className={styles.infoContainer}>
        <div className={styles.infoInnerContainer}>
          <h2 style={{ margin: 0, padding: "0 0.5rem" }}>Loading...</h2>
        </div>
      </div>
    )
  }

  if (!workbook) {
    return <FallbackViewer fileUrl={fileUrl} />
  }

  return (
    <div className={styles.excelViewerContainer}>
      {workbook.sheetNames.length > 1 ? (
        <div className={styles.excelTabs}>
          {workbook.sheetNames.map((name, idx) => (
            <button
              key={name}
              aria-pressed={activeSheetIndex === idx}
              className={activeSheetIndex === idx ? styles.excelTabActive : styles.excelTab}
              type="button"
              onClick={() => setActiveSheetIndex(idx)}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}
      <div ref={spreadsheetWrapperRef} className={styles.excelSpreadsheetWrapper}>
        <div className={styles.excelSpreadsheetGrid}>
          <Spreadsheet className={styles.spreadSheetReadOnly} data={currentData} />
        </div>
      </div>
    </div>
  )
}
